'use client';

import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { createClient } from '../lib/supabase/client';

interface Profile {
  id: string;
  role: string;
  tenant_id: string | null;
  full_name: string | null;
  is_active: boolean;
}

interface Tenant {
  id: string;
  company_name: string;
  slug: string;
  plan: string;
  status: string;
  onboarding_completed: boolean;
  [key: string]: any;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: Profile | null;
  tenant: Tenant | null;
  loading: boolean;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

async function loadProfileAndTenant(userId: string, supabase: any): Promise<{ profile: Profile | null; tenant: Tenant | null }> {
  try {
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (profileError || !profile) {
      return { profile: null, tenant: null };
    }

    let tenant: Tenant | null = null;
    if (profile.role !== 'superadmin' && profile.tenant_id) {
      const { data: tenantData } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', profile.tenant_id)
        .single();
      tenant = tenantData || null;
    }

    return { profile, tenant };
  } catch (e) {
    console.error('Profile load error:', e);
    return { profile: null, tenant: null };
  }
}

/** Clear all Supabase-related storage to prevent stale session caching */
function clearAllSupabaseStorage() {
  if (typeof window === 'undefined') return;
  try {
    // Clear localStorage keys starting with sb- or sb_
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.startsWith('sb_') || key.includes('supabase'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {}

  // Clear cookies with sb- prefix
  if (typeof document !== 'undefined') {
    document.cookie.split(';').forEach((c) => {
      const name = c.trim().split('=')[0];
      if (name && (name.startsWith('sb-') || name.includes('supabase'))) {
        document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=None; Secure`;
        document.cookie = `${name}=; Max-Age=0; Path=/`;
      }
    });
  }
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const loadingProfileRef = useRef(false);

  useEffect(() => {
    const supabase = createClient();
    if (!supabase) {
      setLoading(false);
      return;
    }

    let mounted = true;

    async function handleSession(currentSession: any) {
      if (!currentSession?.user) {
        if (mounted) {
          setSession(null);
          setUser(null);
          setProfile(null);
          setTenant(null);
        }
        return;
      }

      if (mounted) {
        setSession(currentSession);
        setUser(currentSession.user);
      }

      // Prevent duplicate profile loads
      if (loadingProfileRef.current) return;
      loadingProfileRef.current = true;

      try {
        const { profile: p, tenant: t } = await loadProfileAndTenant(currentSession.user.id, supabase);
        if (mounted) {
          setProfile(p);
          setTenant(t);
        }
      } finally {
        loadingProfileRef.current = false;
      }
    }

    async function init() {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!mounted) return;
        await handleSession(currentSession);
      } catch (e) {
        console.error('Auth init error:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: string, newSession: any) => {
        if (!mounted) return;

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED' || event === 'INITIAL_SESSION') {
          if (newSession?.user) {
            if (mounted) {
              setSession(newSession);
              setUser(newSession.user);
            }
            // Only reload profile if user changed or profile is missing
            const currentUserId = newSession.user.id;
            loadingProfileRef.current = false; // reset so we can reload
            const { profile: p, tenant: t } = await loadProfileAndTenant(currentUserId, supabase);
            if (mounted) {
              setProfile(p);
              setTenant(t);
              setLoading(false);
            }
          }
        } else if (event === 'SIGNED_OUT') {
          if (mounted) {
            setSession(null);
            setUser(null);
            setProfile(null);
            setTenant(null);
            setLoading(false);
          }
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const supabase = createClient();
    const { data, error } = await supabase!.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          role: metadata?.role || 'sales',
          tenant_id: metadata?.tenantId || null,
        },
      }
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const supabase = createClient();
    if (!supabase) throw new Error('No se pudo conectar con el servidor. Intenta de nuevo.');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const supabase = createClient();
    try {
      // Sign out from Supabase (global scope to invalidate all sessions)
      await supabase!.auth.signOut({ scope: 'local' });
    } catch (e) {
      console.error('SignOut error:', e);
    } finally {
      // Always clear local state regardless of API response
      setUser(null);
      setSession(null);
      setProfile(null);
      setTenant(null);
      // Clear all cached Supabase storage to prevent stale session on next login
      clearAllSupabaseStorage();
    }
  };

  const getCurrentUser = async () => {
    const supabase = createClient();
    const { data: { user: u }, error } = await supabase!.auth.getUser();
    if (error) throw error;
    return u;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async () => {
    if (!user) return null;
    const supabase = createClient();
    const { data, error } = await supabase!
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    tenant,
    loading,
    isAuthenticated: !!user,
    isSuperAdmin: profile?.role === 'superadmin',
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
